import React, { forwardRef, memo, Component, isValidElement } from 'react';
import { BoxIcon } from 'lucide-react';
type StatColor = 'blue' | 'emerald' | 'amber' | 'purple' | 'red' | 'rose' | 'pink' | 'indigo' | 'teal' | 'gray';
const COLOR_MAP: Record<StatColor, {
  bg: string;
  icon: string;
  value: string;
}> = {
  blue: {
    bg: 'bg-blue-50',
    icon: 'text-blue-500',
    value: 'text-blue-700'
  },
  emerald: {
    bg: 'bg-emerald-50',
    icon: 'text-emerald-500',
    value: 'text-emerald-700'
  },
  amber: {
    bg: 'bg-amber-50',
    icon: 'text-amber-500',
    value: 'text-amber-700'
  },
  purple: {
    bg: 'bg-purple-50',
    icon: 'text-purple-500',
    value: 'text-purple-700'
  },
  red: {
    bg: 'bg-red-50',
    icon: 'text-red-500',
    value: 'text-red-700'
  },
  rose: {
    bg: 'bg-rose-50',
    icon: 'text-rose-500',
    value: 'text-rose-700'
  },
  pink: {
    bg: 'bg-pink-50',
    icon: 'text-pink-500',
    value: 'text-pink-700'
  },
  indigo: {
    bg: 'bg-indigo-50',
    icon: 'text-indigo-500',
    value: 'text-indigo-700'
  },
  teal: {
    bg: 'bg-teal-50',
    icon: 'text-teal-500',
    value: 'text-teal-700'
  },
  gray: {
    bg: 'bg-slate-100',
    icon: 'text-slate-500',
    value: 'text-slate-700'
  }
};
interface StatCardProps {
  /** Primary label (also accepts `title` for backwards compat) */
  label?: string;
  title?: string;
  value: string | number;
  /** Supporting text below value (also accepts `subtitle`) */
  description?: string;
  subtitle?: string;
  icon?: React.ReactNode | typeof BoxIcon;
  color?: StatColor;
  /** Trend object OR legacy string props */
  trend?: {
    value: number;
    label: string;
  } | 'up' | 'down' | 'neutral';
  trendValue?: string;
}
export function StatCard({
  label,
  title,
  value,
  description,
  subtitle,
  icon,
  color = 'blue',
  trend,
  trendValue
}: StatCardProps) {
  const colors = COLOR_MAP[color] ?? COLOR_MAP.blue;
  const displayLabel = label ?? title ?? '';
  const displayDescription = description ?? subtitle;
  // Render icon: support both ReactNode and LucideIcon component reference
  const iconNode: React.ReactNode = (() => {
    if (!icon) return null;
    // Handle function components (regular React components)
    if (typeof icon === 'function') {
      const IconComp = icon as ComponentType;
      return <IconComp data-id="element-5315" />;
    }
    // Handle forwardRef / memo wrapped components (objects with $$typeof and render)
    if (typeof icon === 'object' && icon !== null && '$$typeof' in (icon as Record<string, unknown>) && !isValidElement(icon)) {
      const IconComp = icon as unknown as ComponentType;
      return <IconComp data-id="element-5316" />;
    }
    return icon as React.ReactNode;
  })();
  // Normalise trend to display values
  const trendDisplay = (() => {
    if (!trend || trend === 'neutral') return null;
    if (trend === 'up') return {
      positive: true,
      label: trendValue ?? ''
    };
    if (trend === 'down') return {
      positive: false,
      label: trendValue ?? ''
    };
    if (typeof trend === 'object') {
      return {
        positive: trend.value >= 0,
        label: `${Math.abs(trend.value)}% ${trend.label}`
      };
    }
    return null;
  })();
  return <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4 flex flex-col gap-3 shadow-sm" data-id="element-5317">
      <div className="flex items-start justify-between" data-id="element-5318">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide leading-tight" data-id="element-5319">
          {displayLabel}
        </span>
        {iconNode && <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0 ${colors.icon}`} data-id="element-5320">
            {iconNode}
          </div>}
      </div>
      <div data-id="element-5321">
        <div className={`text-xl sm:text-2xl font-bold ${colors.value} tabular-nums`} data-id="element-5322">
          {value}
        </div>
        {displayDescription && <div className="text-xs text-slate-400 mt-0.5" data-id="element-5323">
            {displayDescription}
          </div>}
        {trendDisplay && <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${trendDisplay.positive ? 'text-emerald-600' : 'text-red-600'}`} data-id="element-5324">
            <span data-id="element-5325">{trendDisplay.positive ? '↑' : '↓'}</span>
            {trendDisplay.label && <span className="text-slate-400 font-normal" data-id="element-5326">
                {trendDisplay.label}
              </span>}
          </div>}
      </div>
    </div>;
}